import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { CheckIcon } from "@/components/icons/check-icon";
import { CloseIcon } from "@/components/icons/close-icon";
import { CopyIcon } from "@/components/icons/copy-icon";
import { MoreIcon } from "@/components/icons/more-icon";
import { SwapIcon } from "@/components/icons/swap-icon";
import { useRemoveMealFromSlot } from "@/hooks/requests/plan/use-remove-meal-from-slot";
import { useToast } from "@/hooks/ui/use-toast";
import { MealPlan } from "@/pages/plans/types/meal-plan.type";
import { cn } from "@/utils/cn";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSwapPlanData } from "../../hooks/use-swap-plan-data";
import { ContextMenu, ContextMenuItem } from "../context-menu";
import { RepeatMealModal } from "../repeat-meal-modal";

interface MealCardActionProps {
  meal: Meal;
  mealTime: MealTime;
  mealDay: string;
  targetDate?: string;
  mealIndex: number;
  isTickedOff: boolean | undefined;
  disabled: boolean;
  isExtraMeal?: boolean;
}

export const MealCardAction = memo(function MealCardAction({
  meal,
  mealTime,
  mealDay,
  targetDate,
  mealIndex,
  isTickedOff,
  disabled,
  isExtraMeal = false,
}: MealCardActionProps) {
  const [isTicked, setIsTicked] = useState(isTickedOff);
  const [isRepeatModalOpen, setIsRepeatModalOpen] = useState(false);
  const { onSwapClick } = useSwapPlanData();
  const { removeMealFromSlotAsync, isLoading: isRemoving } = useRemoveMealFromSlot();
  const { showSuccess, showError } = useToast();

  const handleSwap = useCallback(() => {
    const mealPlan: MealPlan = {
      meal,
      mealTime,
      mealDay,
      targetDate,
      mealIndex,
      isExtraMeal,
    };
    onSwapClick(mealPlan);
  }, [meal, mealTime, mealDay, targetDate, mealIndex, isExtraMeal, onSwapClick]);

  const handleRepeat = useCallback(() => {
    setIsRepeatModalOpen(true);
  }, []);

  const handleDismissRepeat = useCallback(() => {
    setIsRepeatModalOpen(false);
  }, []);

  const handleRemove = useCallback(async () => {
    try {
      await removeMealFromSlotAsync({
        day: mealDay,
        mealTime,
        meal_id: meal.id ?? "",
        targetDate: targetDate ?? undefined,
      });
      showSuccess("Meal removed");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to remove meal. Please try again.");
    }
  }, [meal.id, mealDay, mealTime, targetDate, removeMealFromSlotAsync, showSuccess, showError]);

  const menuItems = useMemo<ContextMenuItem[]>(
    () => [
      {
        label: "Swap Meal",
        icon: <SwapIcon className="h-4 w-4 text-black" />,
        onClick: handleSwap,
      },
      {
        label: "Repeat Meal",
        icon: <CopyIcon className="h-4 w-4" />,
        onClick: handleRepeat,
      },
      {
        label: "Remove Meal",
        icon: <CloseIcon className="h-4 w-4 fill-black" />,
        onClick: () => {
          void handleRemove();
        },
      },
    ],
    [handleSwap, handleRepeat, handleRemove],
  );

  useEffect(() => {
    if (!isTickedOff) {
      setIsTicked(false);
      return;
    }

    setIsTicked(false);
    const timeoutId = setTimeout(() => {
      setIsTicked(true);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isTickedOff]);

  return (
    <>
      {!isTickedOff ? (
        <div onClick={(e) => e.stopPropagation()}>
          <ContextMenu
            trigger={
              <button
                type="button"
                aria-label="Meal actions"
                disabled={disabled || isRemoving}
                className={cn(
                  "flex h-11 w-11 items-center justify-center",
                  (disabled || isRemoving) && "opacity-50",
                )}
              >
                <MoreIcon className="h-4 w-4 text-black" />
              </button>
            }
            items={menuItems}
          />
        </div>
      ) : (
        <div
          className={cn(
            "absolute inset-0 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            "flex items-center justify-center rounded-full transition-all duration-1000",
            {
              "border-border-li-active/5 h-16.5 w-16.5 border": !isTicked,
              "h-11 w-11 border-none bg-[radial-gradient(50%_50%_at_50%_50%,_#E0CDFB_0%,_#F3F1F8_100%)]":
                isTicked,
            },
          )}
        >
          <CheckIcon className="h-5 w-5" />
        </div>
      )}
      <RepeatMealModal
        isOpen={isRepeatModalOpen}
        onDismiss={handleDismissRepeat}
        meal={meal}
        mealTime={mealTime}
        mealDay={mealDay}
        targetDate={targetDate}
      />
    </>
  );
});
